import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { getMenuItems, addMenuItem, updateMenuItem, deleteMenuItem } from '../../services/restaurantService';

const CATEGORIES = [
  'Meats',
  'Sides',
  'Drinks',
  'Desserts',
  'Combos',
  'Appetizers',
  'Specials'
];

function MenuItemsEditor({ restaurantId, onChange }) {
  const [menuItems, setMenuItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Meats',
    isAvailable: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load menu items if restaurantId is provided
  useEffect(() => {
    if (restaurantId) {
      fetchMenuItems();
    }
  }, [restaurantId]);

  const fetchMenuItems = async () => {
    if (!restaurantId) return;

    setLoading(true);
    setError(null);
    
    try {
      const items = await getMenuItems(restaurantId);
      console.log("Fetched menu items:", items); 
      setMenuItems(items);
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setError('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      // Ensure isAvailable is a boolean when editing
      const editItem = {
        ...item,
        isAvailable: item.isAvailable === undefined ? true : Boolean(item.isAvailable)
      };
      console.log("Opening dialog to edit item:", editItem);
      setCurrentItem(editItem);
      setIsEditing(true);
    } else {
      setCurrentItem({
        name: '',
        description: '',
        price: '',
        category: 'Meats',
        isAvailable: true
      });
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentItem(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleToggleAvailability = () => {
    setCurrentItem(prev => {
      const newValue = !prev.isAvailable;
      console.log(`Toggling isAvailable from ${prev.isAvailable} to ${newValue}`);
      return {
        ...prev,
        isAvailable: newValue
      };
    });
  };

  const handleSaveItem = async () => {
    if (!restaurantId) {
      setError('Please save the restaurant first before adding menu items');
      return;
    }
    
    // Validate required fields
    if (!currentItem.name.trim() || !currentItem.price) {
      setError('Name and price are required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Ensure all fields are properly formatted
      const itemData = {
        name: currentItem.name.trim(),
        description: currentItem.description.trim(),
        price: parseFloat(currentItem.price),
        category: currentItem.category,
        isAvailable: Boolean(currentItem.isAvailable)
      };
      
      console.log("About to save menu item with data:", itemData);
      console.log("isAvailable value being saved:", itemData.isAvailable);
      console.log("isAvailable type:", typeof itemData.isAvailable);
      
      if (isEditing) {
        // Update existing menu item
        const updatedItemId = await updateMenuItem(restaurantId, currentItem.id, itemData);
        console.log(`Updated menu item with ID: ${updatedItemId}`);
      } else {
        // Add new menu item
        const newItemId = await addMenuItem(restaurantId, itemData);
        console.log(`Added new menu item with ID: ${newItemId}`);
      }
      
      setSuccess(true);
      setDialogOpen(false);
      
      // Refresh menu items to ensure we have the latest data
      await fetchMenuItems();
      
      if (onChange) {
        onChange(menuItems);
      }
    } catch (err) {
      console.error('Error saving menu item:', err);
      setError(`Failed to save menu item: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!restaurantId) return;
    
    setLoading(true);
    
    try {
      await deleteMenuItem(restaurantId, id);
      console.log(`Deleted menu item with ID: ${id}`);
      
      // Update local state
      const updatedItems = menuItems.filter(item => item.id !== id);
      setMenuItems(updatedItems);
      setSuccess(true);
      
      if (onChange) {
        onChange(updatedItems);
      }
    } catch (err) {
      console.error('Error deleting menu item:', err);
      setError(`Failed to delete menu item: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  // Group menu items by category
  const menuItemsByCategory = menuItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  if (loading && menuItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">
          {menuItems.length} items
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={!restaurantId}
        >
          Add Menu Item
        </Button>
      </Box>

      {menuItems.length === 0 ? (
        <Typography align="center" color="text.secondary" sx={{ my: 4 }}>
          No menu items added yet. Click the "Add Menu Item" button to get started.
        </Typography>
      ) : (
        Object.keys(menuItemsByCategory).map(category => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {category}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menuItemsByCategory[category].map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        {item.description ? (
                          item.description
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            No description
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        ${typeof item.price === 'number' ? item.price.toFixed(2) : parseFloat(item.price).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={item.isAvailable ? "Available" : "Unavailable"} 
                          color={item.isAvailable ? "success" : "error"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenDialog(item)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      )}

      {/* Add/Edit Menu Item Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edit Menu Item' : 'Add Menu Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Item Name"
                name="name"
                value={currentItem.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={currentItem.category || 'Meats'}
                  onChange={handleInputChange}
                  label="Category"
                >
                  {CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Price"
                name="price"
                type="number"
                value={currentItem.price}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: '$',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={currentItem.description}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  Item Status:
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(currentItem.isAvailable)}
                      onChange={handleToggleAvailability}
                      color={currentItem.isAvailable ? "success" : "error"}
                    />
                  }
                  label={currentItem.isAvailable ? "Available" : "Unavailable"}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDialog}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveItem} 
            variant="contained" 
            color="primary"
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
            disabled={loading || !currentItem.name.trim() || !currentItem.price}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error/Success messages */}
      <Snackbar 
        open={!!error || success} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? 'error' : 'success'} 
          variant="filled"
        >
          {error || 'Menu item saved successfully'}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MenuItemsEditor;